import React, {
  ElementType,
  forwardRef,
  JSX,
  ReactNode,
  Ref,
  RefAttributes,
  SyntheticEvent,
  useCallback,
  useMemo,
  useRef,
} from 'react';

import {
  Autocomplete,
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteFreeSoloValueMapping,
  AutocompleteProps,
  AutocompleteValue,
  Box,
  Button,
  Checkbox,
  ChipTypeMap,
  createFilterOptions,
  Stack,
  TextField,
  TextFieldProps,
  Typography,
  useForkRef,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import {
  Control,
  FieldError,
  FieldPath,
  FieldValues,
  PathValue,
  useController,
  UseControllerProps,
} from 'react-hook-form';

import { useFormError } from './FormErrorProvider';
import { useTransform } from './useTransform';
import { propertyExists } from './utils';
import { groupedOptionsFiltering } from '../../Search/Components/helper';

type AutoDefault = {
  id: string | number; // must keep id in case of keepObject
  label: string;
};

const filter = createFilterOptions();

export type AutocompleteElementProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TValue = AutoDefault | string | any,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
  ChipComponent extends ElementType = ChipTypeMap['defaultComponent'],
> = {
  name: TName;
  control?: Control<TFieldValues>;
  options: TValue[];
  loading?: boolean;
  multiple?: Multiple;
  loadingIndicator?: ReactNode;
  rules?: UseControllerProps<TFieldValues, TName>['rules'];
  parseError?: (error: FieldError) => ReactNode;
  required?: boolean;
  label?: TextFieldProps['label'];
  showCheckbox?: boolean;
  matchId?: boolean;
  autocompleteProps?: Omit<
    AutocompleteProps<
      TValue,
      Multiple,
      DisableClearable,
      FreeSolo,
      ChipComponent
    >,
    'name' | 'options' | 'loading' | 'renderInput'
  >;
  textFieldProps?: Omit<TextFieldProps, 'name' | 'required' | 'label'>;
  transform?: {
    input?: (
      value: PathValue<TFieldValues, TName>
    ) => AutocompleteValue<TValue, Multiple, DisableClearable, FreeSolo>;
    output?: (
      event: SyntheticEvent,
      value: AutocompleteValue<TValue, Multiple, DisableClearable, FreeSolo>,
      reason: AutocompleteChangeReason,
      details?: AutocompleteChangeDetails<TValue>
    ) => PathValue<TFieldValues, TName>;
  };
  value?: any;
  labelKey?: string;
  valueKey?: string;
  countKey?: string;
  fixedValues?: any;
  readOnly?: boolean;
  canCreateNew?: boolean | ((typedValue: string, options: any[]) => boolean);
  createNewText?: string;
  canSelectAll?: boolean;
  addNewValue?: (value: string) => void;
  CustomInput?: React.ElementType;
  disableCloseOnSelect?: boolean;
  renderProgress?: (value: number) => React.ReactElement;
  renderAction?: (value: string | any) => React.ReactElement;
  isGroupByOptions?: boolean;
  groupValue?: string;
  onChange?: (
    event: any,
    newValue: AutocompleteValue<TValue, Multiple, DisableClearable, FreeSolo>,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<TValue>
  ) => void;
  groupedOptions?: boolean;
  isFilterCustomComponent?: boolean;
  optionsSx?: any;
};

type AutocompleteElementComponent = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TValue = AutoDefault | string | any, // Maybe we can change it to 'unknown' type for more strict type checking
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
  ChipComponent extends ElementType = ChipTypeMap['defaultComponent'],
>(
  props: AutocompleteElementProps<
    TFieldValues,
    TName,
    TValue,
    Multiple,
    DisableClearable,
    FreeSolo,
    ChipComponent
  > &
    RefAttributes<HTMLDivElement>
) => JSX.Element;

const AutocompleteElement = forwardRef(function AutocompleteElement<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TValue = AutoDefault | string | any,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
  ChipComponent extends ElementType = ChipTypeMap['defaultComponent'],
>(
  props: AutocompleteElementProps<
    TFieldValues,
    TName,
    TValue,
    Multiple,
    DisableClearable,
    FreeSolo,
    ChipComponent
  >,
  ref: Ref<HTMLDivElement>
) {
  const {
    textFieldProps,
    autocompleteProps,
    name,
    control,
    options,
    loading,
    showCheckbox,
    rules,
    loadingIndicator,
    required,
    multiple,
    label,
    parseError,
    transform,
    matchId,
    readOnly = false,
    labelKey = 'name',
    valueKey = 'id',
    countKey = 'count',
    canCreateNew = false,
    createNewText = 'Create new +',
    addNewValue,
    CustomInput,
    renderProgress,
    renderAction,
    canSelectAll,
    onChange,
    isGroupByOptions = false,
    groupValue = 'assignee_type',
    fixedValues = [],
    groupedOptions = false,
    isFilterCustomComponent,
    optionsSx,
  } = props;
  const listboxRef = useRef<HTMLUListElement | null>(null);
  const scrollTopRef = useRef<number>(0);

  const errorMsgFn = useFormError();
  const customErrorFn = parseError || errorMsgFn;

  const validationRules = {
    ...rules,
    ...(required && {
      required: rules?.required || 'This field is required',
    }),
  };

  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    disabled: autocompleteProps?.disabled,
    rules: validationRules,
  });

  const getOptionLabel = (
    option: TValue | AutocompleteFreeSoloValueMapping<FreeSolo>
  ): string => {
    if (typeof autocompleteProps?.getOptionLabel === 'function') {
      return autocompleteProps.getOptionLabel(option);
    }
    if (propertyExists(option, labelKey)) {
      return `${option[labelKey]}`;
    }
    return `${option}`;
  };

  const isOptionEqualToValue = (option: TValue, value: TValue): boolean => {
    if (typeof autocompleteProps?.isOptionEqualToValue == 'function') {
      return autocompleteProps.isOptionEqualToValue(option, value);
    }
    const optionKey = propertyExists(option, valueKey)
      ? option[valueKey]
      : option;
    const newValueKey = propertyExists(value, valueKey)
      ? value[valueKey]
      : value;
    return optionKey === newValueKey;
  };

  const matchOptionByValue = (currentValue: TValue) => {
    return options?.find((option) => {
      if (matchId && propertyExists(option, valueKey)) {
        return option?.[valueKey] === currentValue;
      }
      return isOptionEqualToValue(option, currentValue);
    });
  };

  const { value, onChange: transformedOnChange } = useTransform<
    TFieldValues,
    TName,
    AutocompleteValue<TValue, Multiple, DisableClearable, FreeSolo>
  >({
    value: field.value,
    onChange: field.onChange,
    transform: {
      input:
        typeof transform?.input === 'function'
          ? transform.input
          : (newValue) => {
              return (
                multiple
                  ? (Array.isArray(newValue) ? newValue : []).map(
                      matchOptionByValue
                    )
                  : (matchOptionByValue(newValue) ?? null)
              ) as AutocompleteValue<
                TValue,
                Multiple,
                DisableClearable,
                FreeSolo
              >;
            },
      output:
        typeof transform?.output === 'function'
          ? transform.output
          : (
              _event: SyntheticEvent,
              newValue: AutocompleteValue<
                TValue,
                Multiple,
                DisableClearable,
                FreeSolo
              >
            ) => {
              if (multiple) {
                const newValues = Array.isArray(newValue) ? newValue : [];
                return (
                  matchId
                    ? newValues.map((currentValue) =>
                        propertyExists(currentValue, valueKey)
                          ? currentValue[valueKey]
                          : currentValue
                      )
                    : newValues
                ) as PathValue<TFieldValues, TName>;
              }
              return (
                matchId && propertyExists(newValue, valueKey)
                  ? newValue[valueKey]
                  : newValue
              ) as PathValue<TFieldValues, TName>;
            },
    },
  });

  const handleInputRef = useForkRef(field.ref, textFieldProps?.inputRef);

  const loadingElement = loadingIndicator || (
    <CircularProgress color="inherit" size={20} />
  );

  const handleSelectAll = (event: SyntheticEvent, newValue: boolean) => {
    const allOptions = options?.map((option) =>
      propertyExists(option, valueKey) ? option[valueKey] : option
    );

    const hasSelection = Array.isArray(value) && value.length > 0;
    const updatedValue: AutocompleteValue<
      TValue,
      Multiple,
      DisableClearable,
      FreeSolo
    > = hasSelection
      ? ([] as AutocompleteValue<TValue, Multiple, DisableClearable, FreeSolo>)
      : (allOptions as AutocompleteValue<
          TValue,
          Multiple,
          DisableClearable,
          FreeSolo
        >);

    const reason: AutocompleteChangeReason = newValue
      ? 'selectOption'
      : 'removeOption';

    transformedOnChange(event, updatedValue, reason);
    if (typeof onChange === 'function') {
      onChange(event, updatedValue, reason);
    }
  };

  const isSelectAllChecked =
    options?.length > 0 &&
    options?.every((option) => {
      if (Array.isArray(value)) {
        return (value as TValue[]).some((val) =>
          isOptionEqualToValue(option, val)
        );
      } else {
        return isOptionEqualToValue(option, value as TValue);
      }
    });

  const isSelectAllIndeterminate =
    options?.length > 0 &&
    options?.some((option) => {
      if (Array.isArray(value)) {
        return (value as TValue[]).some((val) =>
          isOptionEqualToValue(option, val)
        );
      } else {
        return isOptionEqualToValue(option, value as TValue);
      }
    }) &&
    !isSelectAllChecked;

  const selectAllOption = {
    isSelectAll: true,
  };

  const combinedOptions = useMemo(() => {
    return canSelectAll ? [selectAllOption, ...options] : options;
  }, [canSelectAll, selectAllOption, options]);

  const processedOptions = useMemo(
    () =>
      groupedOptions ? groupedOptionsFiltering(options, labelKey) : options,
    [groupedOptions, options, labelKey]
  );

  const checkGroupChildren = (groupKey: string) => {
    const children = processedOptions?.filter(
      (option: any) => option?.isChild && option?.group_key === groupKey
    );
    if (!children.length) return false;
    return children?.every((child) =>
      Array.isArray(value)
        ? value?.some((v: any) => isOptionEqualToValue(child, v))
        : false
    );
  };

  const handleParentCheck = (groupKey: string, checked: boolean) => {
    const children = processedOptions.filter(
      (option: any) => option?.isChild && option?.group_key === groupKey
    );
    let newValues = Array.isArray(value) ? [...value] : [];
    if (checked) {
      children?.forEach((child) => {
        if (!newValues?.some((v: any) => isOptionEqualToValue(child, v))) {
          newValues.push(child);
        }
      });
    } else {
      newValues = newValues?.filter(
        (v: any) => !children?.some((child) => isOptionEqualToValue(child, v))
      );
    }
    transformedOnChange({ target: { name } }, newValues, 'selectOption');
    if (typeof onChange === 'function') {
      onChange(
        { target: { name } } as any,
        newValues as AutocompleteValue<
          TValue,
          Multiple,
          DisableClearable,
          FreeSolo
        >,
        'selectOption'
      );
    }
  };

  const customFilterOptions = useCallback(
    (options: any[], params: any) => {
      const input = (params?.inputValue ?? '').trim().toLowerCase();
      if (groupedOptions) {
        const filteredChildren = options?.filter((option: any) => {
          if (option?.isParent) return false;
          return (option[labelKey] || '').toLowerCase().includes(input);
        });

        const groupMap: Record<string, { parent: any; children: any[] }> = {};
        filteredChildren?.forEach((child: any) => {
          const groupKey = child?.group_key;
          if (!groupMap[groupKey]) {
            const parent = options?.find(
              (option: any) =>
                option?.isParent && option?.group_key === groupKey
            );
            if (!parent) return;
            groupMap[groupKey] = {
              parent,
              children: [],
            };
          }
          groupMap[groupKey]?.children.push(child);
        });

        const sortedGroups = Object.values(groupMap).sort((a, b) => {
          const parentA = (a.parent[labelKey] || '').toLowerCase();
          const parentB = (b.parent[labelKey] || '').toLowerCase();

          const aParentStarts = parentA.startsWith(input);
          const bParentStarts = parentB.startsWith(input);

          if (aParentStarts !== bParentStarts) {
            return aParentStarts ? -1 : 1;
          }

          const aChildStarts = a.children.some((c: any) =>
            (c[labelKey] || '').toLowerCase().startsWith(input)
          );

          const bChildStarts = b.children.some((c: any) =>
            (c[labelKey] || '').toLowerCase().startsWith(input)
          );

          if (aChildStarts !== bChildStarts) {
            return aChildStarts ? -1 : 1;
          }

          return parentA.localeCompare(parentB);
        });

        const finalOptions: any[] = [];
        sortedGroups.forEach(({ parent, children }) => {
          finalOptions.push(parent, ...children);
        });

        if (
          input &&
          canCreateNew &&
          !filteredChildren.some(
            (opt: any) => (opt[labelKey] || '').toLowerCase() === input
          )
        ) {
          finalOptions.unshift({ newValue: input });
        }
        return finalOptions;
      }

      // default logic
      const newParams: any = {
        ...params,
        inputValue: params?.inputValue?.trim(),
      };
      const filtered: any = filter(options, newParams);
      if (params.inputValue !== '') {
        const showCreate =
          typeof canCreateNew === 'function'
            ? canCreateNew(newParams.inputValue, options)
            : !!canCreateNew;
        if (showCreate) {
          filtered.unshift({
            newValue: newParams.inputValue,
          });
        }
      }
      return filtered;
    },
    [groupedOptions, labelKey, canCreateNew]
  );

  const listboxProps = isFilterCustomComponent
    ? {
        ref: (node: HTMLUListElement | null) => {
          if (node && 'scrollTop' in node) {
            if (scrollTopRef.current) {
              requestAnimationFrame(() => {
                node.scrollTop = scrollTopRef.current;
              });
            }
            listboxRef.current = node;
          }
        },
        onScroll: () => {
          if (listboxRef.current) {
            scrollTopRef.current = listboxRef.current.scrollTop;
          }
        },
      }
    : undefined;

  const isFieldRequired = rules?.required ? true : required;
  const hasValue = multiple
    ? Array.isArray(value) && value.length > 0
    : value !== null && value !== undefined && value !== '';

  return (
    <Autocomplete
      readOnly={readOnly}
      {...autocompleteProps}
      value={value}
      loading={loading}
      multiple={multiple}
      options={
        groupedOptions && processedOptions?.length
          ? (processedOptions as TValue[])
          : (combinedOptions as TValue[])
      }
      groupBy={
        isGroupByOptions ? (option: any) => option?.[groupValue] : undefined
      }
      renderGroup={
        isGroupByOptions
          ? (params) => (
              <li key={params.key}>
                <Typography
                  color="#88305F"
                  sx={{
                    background: '#FFECF1',
                    p: '8px 12px',
                    textTransform: 'capitalize',
                  }}
                >
                  {params.group}
                </Typography>
                <Typography>{params.children}</Typography>
              </li>
            )
          : undefined
      }
      disableCloseOnSelect={
        typeof autocompleteProps?.disableCloseOnSelect === 'boolean'
          ? autocompleteProps.disableCloseOnSelect
          : !!multiple
      }
      isOptionEqualToValue={isOptionEqualToValue}
      getOptionLabel={getOptionLabel}
      onChange={(event, newValue, reason, details) => {
        if (Array.isArray(newValue)) {
          if (newValue.some((option: any) => option?.isSelectAll)) {
            handleSelectAll(
              event,
              newValue.some((option: any) => option.isSelectAll)
            );
            return;
          }
        }

        let updatedValue = newValue;

        if (fixedValues?.length) {
          const fixedValuesArray = new Set(
            fixedValues?.map((value: any) => value[valueKey])
          );
          const updatedValues = (
            Array.isArray(newValue) ? newValue : []
          ).filter((item) => !fixedValuesArray?.has(item?.[valueKey]));
          updatedValue = [
            ...fixedValues,
            ...updatedValues,
          ] as AutocompleteValue<TValue, Multiple, DisableClearable, FreeSolo>;
        }

        transformedOnChange(event, updatedValue, reason, details);
        if (typeof onChange === 'function') {
          onChange(event, updatedValue, reason, details);
        }
        if (typeof autocompleteProps?.onChange === 'function') {
          autocompleteProps.onChange(event, updatedValue, reason, details);
        }
      }}
      ref={ref}
      filterOptions={customFilterOptions}
      renderOption={
        autocompleteProps?.renderOption ??
        (showCheckbox
          ? (props, option: any, { selected }) => (
              <Box
                key={
                  option?.isParent
                    ? `parent-${option[valueKey]}-${option.group_key}`
                    : option?.isChild
                      ? `child-${option[valueKey]}-${option.group_key}`
                      : propertyExists(option, valueKey)
                        ? option[valueKey]
                        : option
                }
              >
                {option.isSelectAll ? (
                  <Box padding="4px 16px">
                    <Checkbox
                      sx={{ marginRight: 1 }}
                      checked={isSelectAllChecked}
                      indeterminate={isSelectAllIndeterminate}
                      onChange={(e) => handleSelectAll(e, e.target.checked)}
                    />
                    Select All
                  </Box>
                ) : option.newValue ? (
                  <Button
                    key="create-button"
                    variant="text"
                    onClick={() => addNewValue?.(option.newValue)}
                  >
                    {createNewText}
                  </Button>
                ) : groupedOptions && option?.isParent ? (
                  <Box
                    {...props}
                    component="li"
                    key={`label-${getOptionLabel(option)}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      bgcolor: '#F7E8ED',
                      borderRadius: 1,
                    }}
                  >
                    <Checkbox
                      checked={checkGroupChildren(option?.group_key)}
                      onChange={(e) =>
                        handleParentCheck(option?.group_key, e?.target?.checked)
                      }
                      indeterminate={
                        processedOptions
                          .filter(
                            (opt: any) =>
                              opt?.isChild &&
                              opt?.group_key === option?.group_key
                          )
                          .some((child) =>
                            Array.isArray(value)
                              ? value?.some((v: any) =>
                                  isOptionEqualToValue(child, v)
                                )
                              : false
                          ) && !checkGroupChildren(option?.group_key)
                      }
                    />
                    <Typography sx={{ fontWeight: 600 }}>
                      {option[labelKey].charAt(0).toUpperCase() +
                        option[labelKey].slice(1)}
                    </Typography>
                  </Box>
                ) : groupedOptions && option?.isChild ? (
                  <Box
                    {...props}
                    component="li"
                    key={`label-${getOptionLabel(option)}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Checkbox checked={selected} sx={{ ml: 3.5 }} />
                    <Typography>{getOptionLabel(option)}</Typography>
                  </Box>
                ) : (
                  <Stack
                    component="li"
                    {...props}
                    key={`label-${getOptionLabel(option)}`}
                  >
                    <Stack
                      direction="row"
                      width="100%"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Box sx={{ display: 'flex', alignItem: 'center' }}>
                        <Checkbox
                          sx={{ marginRight: 1 }}
                          checked={selected}
                          onChange={(e) => {
                            e.stopPropagation();
                            if (!multiple) {
                              if (selected) {
                                field.onChange(
                                  null as AutocompleteValue<
                                    TValue,
                                    Multiple,
                                    DisableClearable,
                                    FreeSolo
                                  >
                                );
                                if (typeof onChange === 'function') {
                                  onChange(
                                    e,
                                    null as AutocompleteValue<
                                      TValue,
                                      Multiple,
                                      DisableClearable,
                                      FreeSolo
                                    >,
                                    'removeOption'
                                  );
                                }
                                if (
                                  typeof autocompleteProps?.onChange ===
                                  'function'
                                ) {
                                  autocompleteProps.onChange(
                                    e,
                                    null as AutocompleteValue<
                                      TValue,
                                      Multiple,
                                      DisableClearable,
                                      FreeSolo
                                    >,
                                    'removeOption'
                                  );
                                }
                              }
                            }
                          }}
                        />
                        <Typography
                          sx={{
                            marginTop: '10px',
                            wordBreak: 'break-word',
                            ...optionsSx?.label,
                          }}
                        >
                          {getOptionLabel(option)}
                        </Typography>
                        {multiple &&
                          propertyExists(option, countKey) &&
                          ` (${option?.[countKey]})`}
                      </Box>
                      {renderAction?.(option)}
                    </Stack>
                    {multiple &&
                    propertyExists(option, countKey) &&
                    renderProgress ? (
                      <Box width="100%">
                        {renderProgress(option?.[countKey])}
                      </Box>
                    ) : null}
                  </Stack>
                )}
              </Box>
            )
          : undefined)
      }
      onBlur={(event) => {
        field.onBlur();
        if (typeof autocompleteProps?.onBlur === 'function') {
          autocompleteProps.onBlur(event);
        }
      }}
      renderInput={(params) =>
        CustomInput ? (
          <CustomInput {...params} />
        ) : (
          <TextField
            name={name}
            required={isFieldRequired && !hasValue}
            label={label}
            {...textFieldProps}
            {...params}
            error={!!error}
            InputLabelProps={{
              ...params.InputLabelProps,
              ...textFieldProps?.InputLabelProps,
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? loadingElement : null}
                  {params.InputProps.endAdornment}
                </>
              ),
              ...textFieldProps?.InputProps,
            }}
            inputProps={{
              ...params.inputProps,
              ...textFieldProps?.inputProps,
            }}
            helperText={
              error
                ? typeof customErrorFn === 'function'
                  ? customErrorFn(error)
                  : error.message
                : textFieldProps?.helperText
            }
            inputRef={handleInputRef}
          />
        )
      }
      ListboxProps={listboxProps}
    />
  );
});

AutocompleteElement.displayName = 'AutocompleteElement';
export default AutocompleteElement as AutocompleteElementComponent;
